/**
 * UX Audit Phase 2 (Task #2050) — App.tsx split under 200 lines.
 * Task #3408 — App Startup & Loading Experience Overhaul.
 *
 * Routes are extracted into domain slices under `client/src/routes/`.
 * Each slice owns its lazy imports + Route elements.
 */
import { useEffect, useRef, lazy, Suspense, type ReactNode, Component, type ErrorInfo } from "react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { trackPageLoad } from "./utils/performance";
import { initTelemetry, trackPageView, identifyTenant } from "@/lib/telemetry";
import { AppLoadingScreen } from "@/components/AppLoadingScreen";
import { FlagProvider, buildUnleashConfig } from "@/lib/featureFlags";
import {
  ProtectedRoute,
  SuperAdminRoute,
  WorkerRoute,
  FeatureGuardedRoute,
  WorkerFeatureRoute,
  PartnerRoute,
} from "@/components/routing/RouteGate";
import { AliasRedirect, PartnerCommercialRedirect } from "@/components/routing/AliasRedirect";
import { legacyRedirectRoutes } from "@/routes/legacyRedirects";
import { usePermissions } from "@/hooks/use-permission";
import { usePtwOnlyMode } from "@/hooks/usePtwOnlyMode";
import { publicRoutes } from "@/routes/publicRoutes";
import { sharingRoutes } from "@/routes/sharingRoutes";
import { workforceRoutes } from "@/routes/workforceRoutes";
import { PtwRoutes } from "@/routes/ptwRoutes";
import { credentialRoutes } from "@/routes/credentialRoutes";
import { safetyRoutes } from "@/routes/safetyRoutes";
import { equipmentRoutes } from "@/routes/equipmentRoutes";
import { partnerRoutes } from "@/routes/partnerRoutes";
import { workerRoutes } from "@/routes/workerRoutes";
import { superAdminRoutes } from "@/routes/superAdminRoutes";
import { tenantRoutes } from "@/routes/tenantRoutes";
import { AuthProvider, useAuth, type Permission } from "./lib/auth-context";
import { ScopeProvider } from "./lib/scope-context";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CompanyProvider } from "./lib/company-context";
import { CommandPaletteProvider } from "./components/CommandPaletteContext";
import { LocaleProvider } from "./contexts/LocaleProvider";
import { OperationalProfileProvider } from "./lib/operational-profile-context";
import { AppLayoutErrorBoundary } from "./components/layout/AppLayoutErrorBoundary";
import { useLocation } from "wouter";
import { getRoleDashboard } from "@shared/permissions";
import { useQuery } from "@tanstack/react-query";
import { resolvePageTitle } from "@/lib/pageTitles";
import { usePageTitle } from "@/hooks/usePageTitle";
import { TopProgressBar, topProgressStore } from "@/components/routing/TopProgressBar";

const Toaster = lazy(() => import("@/design-system/components/Toaster").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazy(() => import("@/design-system/components/Tooltip").then(m => ({ default: m.TooltipProvider })));
const ImpersonationBanner = lazy(() => import("@/components/layout/ImpersonationBanner").then(m => ({ default: m.ImpersonationBanner })));
const HelpSystem = lazy(() => import("@/components/HelpSystem").then(m => ({ default: m.HelpSystem })));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const NotFound = lazy(() => import("@/pages/not-found"));

function PageTitleManager() {
  const [location] = useLocation();
  const title = resolvePageTitle(location);
  usePageTitle(title);
  return null;
}

/**
 * Task #3408 Step 8: Completes the TopProgressBar after the new route renders.
 * Fires topProgressStore.done() whenever the location settles — meaning the
 * lazy chunk has loaded and React has committed the new page content.
 */
function RouteTracker() {
  const [location] = useLocation();
  const mountedRef = useRef(false);

  useEffect(() => {
    if (mountedRef.current) {
      // Not the initial mount — a route transition just completed.
      topProgressStore.done();
    }
    mountedRef.current = true;
  }, [location]);

  return null;
}

function Router() {
  const [routerLocation] = useLocation();
  const prevLocationRef = useRef(routerLocation);

  useEffect(() => { trackPageLoad(); initTelemetry(); }, []);
  useEffect(() => { trackPageView(routerLocation); }, [routerLocation]);

  // Task #3408 Step 8: start progress bar when location changes.
  // The bar is completed by RouteTracker once the new page has committed.
  useEffect(() => {
    if (routerLocation !== prevLocationRef.current) {
      prevLocationRef.current = routerLocation;
      topProgressStore.start();
    }
  }, [routerLocation]);

  return (
    // Task #3408 Step 3: Suspense fallback is null — the current page stays
    // rendered while the new chunk downloads (TopProgressBar indicates progress).
    <Suspense fallback={null}>
      <RouteTracker />
      <PageTitleManager />
      <Switch>
        {publicRoutes()}
        {sharingRoutes()}
        {workforceRoutes()}
        {PtwRoutes()}
        {credentialRoutes()}
        {safetyRoutes()}
        {equipmentRoutes()}
        {partnerRoutes()}
        {workerRoutes()}
        {superAdminRoutes()}
        {tenantRoutes()}
        {legacyRedirectRoutes()}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AuthGate() {
  const { isAuthenticated, isLoading, isVerifyingSession, isServerStarting, currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const [location] = useLocation();
  const { isPtwOnly } = usePtwOnlyMode();

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      identifyTenant({ tenantId: currentUser?.activeTenant?.id ?? null, role: (currentUser as any)?.role ?? null });
    }
  }, [isAuthenticated, currentUser?.id, currentUser?.activeTenant?.id, (currentUser as any)?.role]);

  const needsRedirect = isAuthenticated && (location === "/auth/login" || location === "/");
  const needsDashboardRedirect = isAuthenticated && isPtwOnly && location === "/tenant/dashboard";
  const { data: navConfigs } = useQuery<any[]>({
    queryKey: ["/api/navigation-config"],
    enabled: needsRedirect && !!currentUser?.role,
    staleTime: 30_000,
    gcTime: 60_000,
  });

  const publicPaths = ["/auth/login", "/auth/mfa", "/auth/mfa-totp", "/auth/expired", "/auth/unauthorized", "/auth/force-change-password", "/invite", "/issuer-invite", "/select-tenant", "/terms", "/privacy"];
  const isPublicPath = publicPaths.some(p => location === p || location.startsWith(p + "/")) || location.startsWith("/v/");

  if (isLoading) {
    // Task #3408 Step 3: only render AppLoadingScreen when the HTML pre-loader
    // has already been dismissed. While #pre is still visible it acts as the
    // sole loading indicator; rendering a duplicate full-screen loader on top
    // would cause a double-flash when the transition fires.
    const preStillVisible = typeof document !== "undefined" && !!document.getElementById("pre");
    if (preStillVisible) return null;
    return <AppLoadingScreen isServerStarting={isServerStarting} />;
  }
  if (!isAuthenticated && !isPublicPath) {
    // PTW-018: a warm-start (returning user) renders the shell immediately with
    // isLoading=false while the background /api/auth/me check is still in flight.
    // Redirecting to /auth/login here would discard the requested deep link and
    // then bounce the user to their role dashboard once the session resolves.
    // While verification is pending, hold on the loading screen so the explicit
    // initial URL survives until the session is confirmed.
    if (isVerifyingSession) {
      const preStillVisible = typeof document !== "undefined" && !!document.getElementById("pre");
      if (preStillVisible) return null;
      return <AppLoadingScreen isServerStarting={isServerStarting} />;
    }
    return <Redirect to="/auth/login" />;
  }
  if (needsDashboardRedirect) return <Redirect to="/ptw" />;
  if (needsRedirect) {
    if (hasPermission("super_admin")) return <Redirect to="/super-admin/dashboard" />;
    if (currentUser?.hasCompletedOnboarding === false) return <Redirect to="/onboarding/welcome" />;
    let landingPage = getRoleDashboard(currentUser?.role);
    if (navConfigs) { const config = navConfigs.find((c: any) => c.role === currentUser?.role); if (config?.landingPage) landingPage = config.landingPage; }
    if (isPtwOnly && !hasPermission("super_admin")) landingPage = "/ptw";
    return <Redirect to={landingPage} />;
  }
  return <AppLayoutErrorBoundary><ThemeProvider><Router /></ThemeProvider></AppLayoutErrorBoundary>;
}

class FlagProviderErrorBoundary extends Component<{ flagChildren: ReactNode; fallbackChildren: ReactNode }, { hasError: boolean }> {
  constructor(props: { flagChildren: ReactNode; fallbackChildren: ReactNode }) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError(): { hasError: boolean } { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.warn("[FlagProvider] Unleash initialization failed, feature flags defaulting to off:", error.message, info.componentStack); }
  render() { return this.state.hasError ? this.props.fallbackChildren : this.props.flagChildren; }
}

function TenantAwareFlagProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const tenantId = currentUser?.activeTenant?.id;
  return <FlagProviderErrorBoundary flagChildren={<FlagProvider config={buildUnleashConfig(tenantId)}>{children}</FlagProvider>} fallbackChildren={children} />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LocaleProvider>
          <TenantAwareFlagProvider>
            <CompanyProvider>
              <OperationalProfileProvider>
              <CommandPaletteProvider>
                {/* Task #3408 Step 8: TopProgressBar rendered outside Suspense so it
                    is always available during chunk downloads. */}
                <TopProgressBar />
                {/* ScopeProvider is hoisted above AuthGate so that usePtwOnlyMode()
                    (called inside AuthGate) can safely call useScope().  It only
                    needs AuthProvider + FlagProvider above it, both of which are
                    already in the tree, and it handles unauthenticated 401s
                    gracefully by calling handleSessionExpired(). */}
                <ScopeProvider>
                {/* Task #3408 Step 3: outer Suspense fallback is null — the pre-loader
                    handles cold-start blank screen; returning users render immediately. */}
                <Suspense fallback={null}>
                  <TooltipProvider>
                    <Suspense fallback={null}><ImpersonationBanner /></Suspense>
                    <Suspense fallback={null}><Toaster /></Suspense>
                    <Suspense fallback={null}><CookieConsent /></Suspense>
                    <Suspense fallback={null}><HelpSystem /></Suspense>
                    <AuthGate />
                  </TooltipProvider>
                </Suspense>
                </ScopeProvider>
              </CommandPaletteProvider>
              </OperationalProfileProvider>
            </CompanyProvider>
          </TenantAwareFlagProvider>
        </LocaleProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;

// Task #3408 Step 19: LoadingFallback is retained as a named export for any
// external consumers that still reference it, but it now delegates to the
// AppLoadingScreen rather than being the authoritative full-screen loader.
export { AppLoadingScreen as LoadingFallback };
